import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Steps } from 'primereact/steps';
import type { MenuItem } from 'primereact/menuitem';
import { useTranslation } from 'react-i18next';
import type { WizardStepProps } from './wizard-step.component';
import Button from '../button/button';
import ReactDOM from 'react-dom';

export interface WizardProps {
  title: string;
  children: Array<React.ReactElement<WizardStepProps>>;
  activeStepIndex?: number;
  disableFinishButton?: boolean;
  // An optional action to execute when the wizard is canceled
  onCancel?: () => void;
  // An optional action to execute when the wizard is completed
  onFinish?: () => void;
  // Whether the wizard should close when the escape key is pressed. Defaults to false.
  closeOnEscape?: boolean;
}

export interface WizardRef {
  open: () => void;
  close: () => void;
}

const Wizard = (
  {
    title,
    children,
    activeStepIndex = 0,
    onCancel,
    onFinish,
    disableFinishButton = false,
    closeOnEscape = false,
  }: WizardProps,
  ref: Ref<WizardRef>
) => {
  const [index, setIndex] = useState<number>(activeStepIndex);

  const visibleChildren = useMemo(() => {
    return children.filter((item) => !item.props.hideStep);
  }, [children]);

  const { onBack, onNext } = visibleChildren[index].props;

  const modalRef = useRef<HTMLDialogElement>(null);

  const { t } = useTranslation();

  const stepLabels: MenuItem[] = useMemo(() => {
    return visibleChildren.map((wizardStep) => {
      return {
        label: wizardStep.props.label ?? '',
      };
    });
  }, [visibleChildren]);

  useEffect(() => {
    const currentModal = modalRef.current;
    return () => {
      currentModal?.close();
    };
  }, []);

  const open = () => {
    modalRef.current?.showModal();
  };

  const close = () => {
    modalRef.current?.close();
  };

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleDialogClose = () => {
    setIndex(0);
  };

  const handleDialogCancel = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (closeOnEscape) {
        close();
        onCancel?.();
      }
    },
    [closeOnEscape, onCancel]
  );

  const handleCancel = () => {
    close();
    onCancel?.();
  };

  const handleBack = () => {
    if (!onBack || onBack() === true) {
      setIndex(Math.max(index - 1, 0));
    }
  };

  const handleNext = () => {
    if (!onNext || onNext() === true) {
      setIndex(Math.min(index + 1, visibleChildren.length - 1));
    }
  };

  const handleFinish = () => {
    close();
    onFinish?.();
  };

  const createDialog = () => {
    return (
      <dialog
        ref={modalRef}
        data-testid="wizard-dialog"
        onCancel={handleDialogCancel}
        onClose={handleDialogClose}
        className="odin-outline-none odin-rounded-md odin-p-4 odin-w-[800px] odin-h-[600px] odin-px-2 odin-overflow-hidden"
      >
        <div className="odin-text-base odin-font-medium odin-text-mono-0 odin-pb-4 odin-px-2 odin-pt-0">{title}</div>
        <Steps className="odin odin-w-full odin-pb-4 odin-px-2" model={stepLabels} activeIndex={index} />
        <div className="odin-flex odin-px-2 odin-flex-grow odin-w-full odin-h-full odin-min-h-[100px] odin-max-h-[400px] odin-text odin-text-gray-2 odin-overflow-y-auto custom-scroll">
          {visibleChildren.map((child, childIndex) => {
            return (
              <div key={stepLabels[childIndex].label} hidden={childIndex !== index} className="odin-w-full">
                {child}
              </div>
            );
          })}
        </div>
        <div className="odin-flex odin-pt-2 odin-w-full odin-gap-4 odin-justify-end odin-px-2">
          <Button onClick={handleCancel}>{t('cancel', 'Cancel')}</Button>
          <Button disabled={index < 1} onClick={handleBack}>
            {t('back', 'Back')}
          </Button>
          <Button disabled={index === visibleChildren.length - 1} onClick={handleNext}>
            {t('next', 'Next')}
          </Button>
          <Button disabled={index < visibleChildren.length - 1 || disableFinishButton} onClick={handleFinish}>
            {t('finish', 'Finish')}
          </Button>
        </div>
      </dialog>
    );
  };

  return ReactDOM.createPortal(createDialog(), document.body);
};

export default forwardRef(Wizard);
